import React, { useEffect } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { usePaymentInputs } from 'react-payment-inputs'
import images from 'react-payment-inputs/images'
import Autocomplete from 'react-google-autocomplete'
import PropTypes from 'prop-types'
import _ from 'lodash'

// Images
import User from '../../assets/images/user.svg'
import Location from '../../assets/images/location.svg'

// Components
import { CheckBox } from '../CheckBox'
import { EditIcon } from '../EditIcon'
import { Modal } from '../Modal'
import { Select } from '../Select'
import { TextInput } from '../TextInput'

// Utils
import { TERMS_AND_CONDITIONS_URL } from '../../utils/constants'
import { toast } from '../../utils/helpers'

import {
  calculateAdjustedPriceTotal,
  calculatePriceTotal,
  getPricingData,
  configureBillingAddress,
} from './helpers'

const PurchaseLicensesModal = ({
  adjustablePricing,
  event,
  cancelButtonLabel,
  heldLicenseCount,
  purchasedLicenseCount,
  existingRentalDeviceCount,
  loading,
  onSubmit,
  showModal,
  setShowModal,
}) => {
  // Read from the current event on every render rather than stored at mount. Event pages
  // are reused when the route's event changes, so a copy taken at mount could keep offering
  // the previous event's rentals (EXEC-1641). Reading it on the first render also means an
  // option the event does not offer is never drawn and then taken away.
  const pricingInfo = getPricingData(event)

  const handleError = (m) => toast(m, 'error')

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm({
    defaultValues: {
      // EE staff open this to sell whatever the exhibitor asked for, which is often
      // rentals alone, so nothing is preselected. The exhibitor-facing flow still
      // starts at one, since it exists to buy that first required license.
      numberOfLicenses: adjustablePricing ? { label: '0', id: 0 } : { label: '1', id: 1 },
      numberOfLeadRetrievalDevices: { label: '0', id: 0 },
      licenseUnitPrice: '',
      rentalUnitPrice: '',
      firstName: '',
      lastName: '',
      creditCard: '',
      cardExpiry: '',
      cardCvc: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
      termsAccepted: false,
    },
  })

  const pricingFormData = useWatch({ control })

  const { meta, getCardImageProps, getCardNumberProps, getExpiryDateProps, getCVCProps } =
    usePaymentInputs()

  useEffect(() => {
    const pricing = getPricingData(event)

    // For adjustable pricing, prefill the editable unit prices from the current tier; every
    // license is charged at the (editable) per-license price, matching the standard cost of
    // one more license for this exhibitor
    if (adjustablePricing) {
      const defaultLicensePrice =
        purchasedLicenseCount > 0 ? pricing.additionalPrice : pricing.firstPrice
      setValue('licenseUnitPrice', defaultLicensePrice ?? '')
      setValue('rentalUnitPrice', pricing.rentalPrice ?? '')
    }
  }, [event?.id])

  // The event settings form requires both license prices, so a missing one means the
  // event has not been priced yet. Either one missing is enough: the API refuses a
  // purchase that needs a price the current tier does not have (EXEC-1641).
  const noPricing =
    !adjustablePricing && (_.isNil(pricingInfo.firstPrice) || _.isNil(pricingInfo.additionalPrice))

  // A blank rental price is how EE turns rentals off for an event, so an unpriced rental is
  // not offered at all — not to the exhibitor, and not to EE staff buying on their behalf.
  // It used to be offered anyway, and buying one failed with Python's own error text
  // (EXEC-1641).
  const rentalsOffered = !_.isNil(pricingInfo.rentalPrice)

  // `id` is also the `value` on the quantity selects
  const licenseQuantity = pricingFormData.numberOfLicenses?.id || 0
  const rentalQuantity = pricingFormData.numberOfLeadRetrievalDevices?.id || 0

  // A hidden rental select must not carry a quantity into the purchase, as it would if the
  // event changed to one without rentals after a quantity was picked
  useEffect(() => {
    if (!rentalsOffered && rentalQuantity > 0) {
      setValue('numberOfLeadRetrievalDevices', { label: '0', id: 0 })
    }
  }, [rentalsOffered, rentalQuantity])
  const adjustedTotal = calculateAdjustedPriceTotal(pricingFormData)

  // A comped EE purchase has nothing to charge, so don't collect a card for it.
  // The exhibitor-facing flow always collects payment details, unchanged.
  const requiresPayment = !adjustablePricing || adjustedTotal > 0

  const nothingSelected = licenseQuantity === 0 && rentalQuantity === 0

  // Every purchase an exhibitor makes for themselves needs its own T&C agreement. EE staff
  // buying on an exhibitor's behalf aren't the party who can agree, so their flow doesn't
  // ask — and the API records no acknowledgment for it either.
  const requiresTermsAgreement = !adjustablePricing
  const termsAccepted = pricingFormData.termsAccepted === true

  // Every rental device needs a license to run on (EXEC-1558). Both sides count what the
  // exhibitor will hold after this purchase; the licenses side is every usable one they
  // already have — free allotment included — plus whatever is in the cart. EE staff are
  // exempt, since their adjusted-price purchase is how legitimate mismatches get settled.
  //
  // The held count, not the purchased one. A free license runs a rental device just as
  // well as a bought one, so this rule counts it. Pricing does not — see the submit
  // label below. Collapsing the two counts into one breaks whichever rule loses.
  const licensesAfterPurchase = heldLicenseCount + licenseQuantity
  const rentalsAfterPurchase = existingRentalDeviceCount + rentalQuantity
  const rentalShortfall = rentalsAfterPurchase - licensesAfterPurchase
  const rentalsExceedLicenses = !adjustablePricing && rentalShortfall > 0

  const configureSubmitLabel = () => {
    if (noPricing) {
      return 'Pricing Not Set'
    }

    if (nothingSelected) {
      return 'Nothing Selected'
    }

    if (adjustablePricing) {
      return adjustedTotal > 0 ? `Purchase ($${adjustedTotal})` : 'Add at No Charge'
    }

    // The purchased count decides whether the first-license price applies, and the API
    // decides the same thing the same way. The two have to agree: the API recomputes
    // the line items and refuses a submitted amount that does not match them.
    return `Purchase ($${calculatePriceTotal(
      pricingFormData,
      pricingInfo,
      purchasedLicenseCount,
    )})`
  }

  return (
    <Modal
      actions={[
        {
          type: 'cancel',
          label: cancelButtonLabel,
          onClick: () => setShowModal(false),
        },
        {
          // An empty cart used to be caught by the gateway refusing a $0 charge; zero-total
          // purchases now complete without it, so both flows have to stop it here
          disabled:
            noPricing ||
            nothingSelected ||
            rentalsExceedLicenses ||
            (requiresTermsAgreement && !termsAccepted),
          type: 'submit',
          label: configureSubmitLabel(),
          onClick: () => handleSubmit(onSubmit)(),
        },
      ]}
      icon={<EditIcon className="h-5 fill-white sm:h-6" />}
      content={
        <div className="mt-3 flex flex-col gap-4 sm:mt-5">
          {adjustablePricing ? (
            <span className="text-sm text-gray-600">
              Choose what this exhibitor is buying, then set the price. Unit prices are pre-filled
              with the event&apos;s current pricing and every license is charged at the price
              entered. Set a price to $0 to add items at no charge.
            </span>
          ) : (
            <div className="text-sm text-gray-600">
              <span className="text-sm text-gray-600">
                In order to start building your Lead Retrieval App and access your leads, you must
                purchase at least one (1) license. Please select from the below and complete your
                purchase.
              </span>
              <div className="mt-5">
                <span className="font-bold">First License</span> -{' '}
                {pricingInfo.firstPrice !== null
                  ? `$${pricingInfo.firstPrice}`
                  : 'Pricing Not Set'}
              </div>
              <div className={rentalsOffered ? '' : 'mb-5'}>
                <span className="font-bold">Additional Licenses</span> -{' '}
                {pricingInfo.additionalPrice !== null
                  ? `$${pricingInfo.additionalPrice}`
                  : 'Pricing Not Set'}
              </div>
              {rentalsOffered && (
                <>
                  <div className="mb-5">
                    <span className="font-bold">Lead Retrieval Device Rental (optional)*</span> -{' '}
                    {`$${pricingInfo.rentalPrice}/rental`}
                  </div>
                  <div className="italic">
                    *A license is what lets you capture leads — use it on your own smartphone, or
                    rent a device to run it on. A rental is the hardware only and does not include
                    a license.
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Controller
              name="numberOfLicenses"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  data-testid="numberOfLicenses"
                  disabled={loading}
                  error={errors.numberOfLicenses && 'This field is required'}
                  fullWidth
                  id="numberOfLicenses"
                  name="numberOfLicenses"
                  nunito
                  onChange={onChange}
                  options={_.map(_.range(0, 51), (i) => ({ id: i, label: `${i}` }))}
                  label="Number of Licenses"
                  placeholder="Select a number of licenses to purchase"
                  style={{ flex: true, width: '100%' }}
                  value={value}
                />
              )}
              rules={{ required: true }}
            />
            {rentalsOffered && (
              <Controller
                name="numberOfLeadRetrievalDevices"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="numberOfLeadRetrievalDevices"
                    disabled={loading}
                    error={errors.numberOfLeadRetrievalDevices && 'This field is required'}
                    fullWidth
                    id="numberOfLeadRetrievalDevices"
                    name="numberOfLeadRetrievalDevices"
                    nunito
                    onChange={onChange}
                    options={_.map(_.range(0, 21), (i) => ({ id: i, label: `${i}` }))}
                    label="Number of Lead Retrieval Device Rental(s) (each needs a license)"
                    placeholder="Select a number of lead retrieval devices to rent"
                    style={{ flex: true, width: '100%' }}
                    value={value}
                  />
                )}
                rules={{ required: true }}
              />
            )}
          </div>

          {/* Exhibitors have turned up on site with rental devices and no license to run
              on them (EXEC-1558). Red and blocking once the cart would leave them short,
              green and advisory the rest of the time so the requirement is clear before
              they pick a quantity that trips it. EE staff see neither — their
              adjusted-price purchase is how legitimate mismatches get settled.

              The advisory shipped amber, which reads as a warning: exhibitors thought
              something was wrong at the exact moment they'd got it right (EXEC-1606).
              Green says "this is covered"; the blocking alert keeps red to itself. */}
          {rentalsExceedLicenses && (
            <div className="rounded-2xl bg-error-light px-4 py-3 text-sm text-error-dark">
              <span className="font-bold">Each rental device needs its own license.</span> This
              purchase would leave you with {rentalsAfterPurchase} device
              {rentalsAfterPurchase === 1 ? ' rental' : ' rentals'} and {licensesAfterPurchase}
              {licensesAfterPurchase === 1 ? ' license' : ' licenses'}. Add {rentalShortfall} more
              {rentalShortfall === 1 ? ' license' : ' licenses'} to continue.
            </div>
          )}

          {!adjustablePricing && rentalQuantity > 0 && !rentalsExceedLicenses && (
            <div className="rounded-2xl bg-success-light px-4 py-3 text-sm text-success-dark">
              Each rental device needs its own license to capture leads, and the rental does not
              include one. You will have {licensesAfterPurchase}
              {licensesAfterPurchase === 1 ? ' license' : ' licenses'} for {rentalsAfterPurchase}
              {rentalsAfterPurchase === 1 ? ' device rental' : ' device rentals'} after this
              purchase.
            </div>
          )}

          {/* Only ask for a unit price for the item types actually being bought */}
          {adjustablePricing && !nothingSelected && (
            <div className="flex flex-col gap-2 sm:flex-row sm:space-x-2">
              {licenseQuantity > 0 && (
                <Controller
                  name="licenseUnitPrice"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      className="rounded-2xl border-gray-550 py-2.5 pl-7 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                      data-testid="licenseUnitPrice"
                      disabled={loading}
                      error={errors.licenseUnitPrice && 'Enter a valid price'}
                      fullWidth
                      icon={<span className="ml-2 text-gray-600">$</span>}
                      id="licenseUnitPrice"
                      inputMode="decimal"
                      label="Price per License"
                      name="licenseUnitPrice"
                      onChange={onChange}
                      placeholder="0.00"
                      value={value}
                    />
                  )}
                  // Validated against the quantity rather than the field's own presence:
                  // react-hook-form keeps unmounted fields registered, so a `required`
                  // rule would otherwise block submit for an item type not being bought
                  rules={{
                    validate: (value) =>
                      licenseQuantity === 0 || /^\d+(\.\d{1,2})?$/.test(String(value ?? '')),
                  }}
                />
              )}
              {rentalQuantity > 0 && (
                <Controller
                  name="rentalUnitPrice"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      className="rounded-2xl border-gray-550 py-2.5 pl-7 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                      data-testid="rentalUnitPrice"
                      disabled={loading}
                      error={errors.rentalUnitPrice && 'Enter a valid price'}
                      fullWidth
                      icon={<span className="ml-2 text-gray-600">$</span>}
                      id="rentalUnitPrice"
                      inputMode="decimal"
                      label="Price per Device Rental"
                      name="rentalUnitPrice"
                      onChange={onChange}
                      placeholder="0.00"
                      value={value}
                    />
                  )}
                  rules={{
                    validate: (value) =>
                      rentalQuantity === 0 || /^\d+(\.\d{1,2})?$/.test(String(value ?? '')),
                  }}
                />
              )}
            </div>
          )}

          {!requiresPayment && (
            <div className="rounded-2xl bg-status-green px-4 py-3 text-sm text-role-admin">
              This purchase totals $0.00, so no payment is needed. The items will be added to the
              exhibitor straight away.
            </div>
          )}

          <div className={`flex flex-col ${requiresPayment ? '' : 'hidden'}`}>
            <span className="text-sm font-medium text-gray-700">Card Details</span>
            <div className="flex gap-2">
              <Controller
                name="firstName"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    className="mr-2 mt-2 rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="firstName"
                    disabled={loading}
                    error={errors.firstName && 'This field is required'}
                    fullWidth
                    icon={<img alt="User" className="ml-2 h-4" src={User} />}
                    id="firstName"
                    onChange={onChange}
                    name="firstName"
                    placeholder="First Name"
                    style={{ flex: true, width: '100%' }}
                    value={value}
                  />
                )}
                rules={{ required: requiresPayment }}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    className="mt-2 rounded-2xl border-gray-550 py-2.5 pl-9 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="lastName"
                    disabled={loading}
                    error={errors.lastName && 'This field is required'}
                    fullWidth
                    icon={<img alt="User" className="ml-2 h-4" src={User} />}
                    id="lastName"
                    onChange={onChange}
                    name="lastName"
                    placeholder="Last Name"
                    value={value}
                  />
                )}
                rules={{ required: requiresPayment }}
              />
            </div>

            <div className="mt-2 flex items-center rounded-2xl border border-gray-550 py-2.5 pl-2 pr-4 focus-within:border-purple">
              <div className="basis-2">
                <svg {...getCardImageProps({ images })} />
              </div>
              <Controller
                name="cardNumber"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    {...getCardNumberProps({ onChange })}
                    containerClassName="w-full"
                    className="sentry-mask border-0 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="cardNumber"
                    disabled={loading}
                    id="cardNumber"
                    name="cardNumber"
                    placeholder="Card number"
                    value={value}
                  />
                )}
                rules={{ required: requiresPayment }}
              />
              <Controller
                name="cardExpiry"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    {...getExpiryDateProps({ onChange })}
                    containerClassName="basis-26"
                    className="sentry-mask border-0 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="cardExpiry"
                    disabled={loading}
                    id="cardExpiry"
                    name="cardExpiry"
                    placeholder="MM / YY"
                    value={value}
                  />
                )}
                rules={{ required: requiresPayment }}
              />
              <Controller
                name="cardCvc"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    {...getCVCProps({ onChange })}
                    containerClassName="basis-26"
                    className="sentry-mask border-0 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="cardCvc"
                    disabled={loading}
                    id="cardCvc"
                    name="cardCvc"
                    placeholder="CVC"
                    value={value}
                  />
                )}
                rules={{ required: requiresPayment }}
              />
            </div>
            {meta.isTouched && meta.error && (
              <div className="mt-1 w-full bg-error-light px-2 py-1">
                <p className="text-center text-sm font-medium text-error-dark">{meta.error}</p>
              </div>
            )}

            {(errors.cardCvc || errors.cardExpiry || errors.cardNumber) && (
              <div className="mt-1 w-full bg-error-light px-2 py-1">
                <p className="text-center text-sm font-medium text-error-dark">
                  This field is required
                </p>
              </div>
            )}

            <div className="mt-5">
              <span className="text-sm font-medium text-gray-700">Billing Address</span>
              <div className="relative mt-2">
                <img
                  alt="Location"
                  className="absolute left-1 top-2.5 ml-1.5 h-5"
                  src={Location}
                />
                <Autocomplete
                  data-testid="billingAddress"
                  className="w-full rounded-2xl border border-gray-550 py-2.5 pl-8 pr-4 text-sm outline-none placeholder:text-gray-600 focus-within:border-purple"
                  apiKey={import.meta.env.VITE_GOOGLE_API_KEY}
                  id="address"
                  name="address"
                  onPlaceSelected={(place) => {
                    // Set the address fields
                    const addressComponents = configureBillingAddress(place)

                    if (addressComponents) {
                      setValue(
                        'address1',
                        `${addressComponents.streetNumber} ${addressComponents.streetName}`,
                      )
                      setValue('city', addressComponents.city)
                      setValue('state', addressComponents.state)
                      setValue('country', addressComponents.country)
                      setValue('zip', addressComponents.zip)
                    } else {
                      handleError('Unable to determine address. Please enter manually.')
                    }
                  }}
                  options={{
                    types: ['address'],
                  }}
                  placeholder="Enter billing address"
                />
              </div>
            </div>

            <div className="my-2 h-[1px] w-[80%] place-self-center bg-gray-400" />

            <div className="flex flex-col gap-2">
              <TextInput
                fullWidth
                className="w-full rounded-2xl border-gray-550 py-2.5  pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                error={errors.address1 && 'This field is required'}
                name="address1"
                placeholder="Address"
                {...register('address1', { required: requiresPayment })}
              />

              <TextInput
                fullWidth
                className="w-full rounded-2xl border-gray-550 py-2.5  pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                error={errors.address2 && 'This field is required'}
                name="address2"
                placeholder="Apartment, Suite, etc."
                {...register('address2')}
              />
              <div className="flex flex-col sm:flex-row sm:space-x-2">
                <TextInput
                  fullWidth
                  className="w-full rounded-2xl border-gray-550 py-2.5  pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  error={errors.city && 'This field is required'}
                  name="city"
                  placeholder="City"
                  {...register('city', { required: requiresPayment })}
                />
                <TextInput
                  fullWidth
                  className="w-full rounded-2xl border-gray-550 py-2.5  pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  error={errors.state && 'This field is required'}
                  name="state"
                  placeholder="State"
                  {...register('state', { required: requiresPayment })}
                />
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-2">
                <TextInput
                  fullWidth
                  className="w-full rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  error={errors.country && 'This field is required'}
                  name="country"
                  placeholder="Country"
                  {...register('country', { required: requiresPayment })}
                />

                <TextInput
                  fullWidth
                  className="w-full rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  error={errors.zip && 'This field is required'}
                  name="zip"
                  placeholder="Zip Code"
                  {...register('zip', { required: requiresPayment })}
                />
              </div>
            </div>
          </div>

          {requiresTermsAgreement && (
            <Controller
              name="termsAccepted"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CheckBox
                  className="items-start"
                  disabled={loading}
                  label={
                    <>
                      I have read and agree to the{' '}
                      <a
                        className="font-bold text-purple underline"
                        href={TERMS_AND_CONDITIONS_URL}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Terms &amp; Conditions
                      </a>
                    </>
                  }
                  name="termsAccepted"
                  onChange={(e) => onChange(e.target.checked)}
                  value={value}
                />
              )}
            />
          )}
        </div>
      }
      loading={loading}
      open={showModal}
      setOpen={setShowModal}
      title="Purchase Licenses"
    />
  )
}

PurchaseLicensesModal.defaultProps = {
  adjustablePricing: false,
  cancelButtonLabel: 'Cancel',
  heldLicenseCount: 0,
  purchasedLicenseCount: 0,
  existingRentalDeviceCount: 0,
  loading: false,
}

PurchaseLicensesModal.propTypes = {
  adjustablePricing: PropTypes.bool,
  cancelButtonLabel: PropTypes.string,
  // Two different counts, because two different rules read them. Both have to be the
  // count across every page, not the page on screen.
  //
  // `heldLicenseCount` — every usable license, free allotment included. Drives the
  // rentals-need-licenses check (EXEC-1558).
  // `purchasedLicenseCount` — the ones the exhibitor bought. Decides whether the
  // first-license price applies, and the API decides it from the same set.
  heldLicenseCount: PropTypes.number,
  purchasedLicenseCount: PropTypes.number,
  existingRentalDeviceCount: PropTypes.number,
  event: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
}

export default PurchaseLicensesModal
